import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { DescriptorJblOptionText } from '@r6digital/rapidstor-types/types/Descriptor';

import { stringifyNumber } from '@/features/rapidstor/helpers';
import { UpgradeDescriptor } from '@/features/rapidstor/UnitOptions';

export interface OptionCopy {
    heading: string;
    /**
     * The card hides the subheading on a descriptor-upgrade row, but it is still
     * resolved so the tracked row can say what the copy would have read.
     */
    subheading: string;
}

export interface ResolveOptionCopyArgs {
    unitType: UnitType;
    /** The descriptor's `jblOptionText`, as the selected card passes it. */
    optionText?: DescriptorJblOptionText;
    /** Set on a descriptor-upgrade row, where the descriptor supplies the copy. */
    upgradeDescriptor?: UpgradeDescriptor;
}

const accessLocation = (unitType: UnitType): string => (
    unitType.units[0]?.iEntryLoc === 1 ? 'Drive Up Access' : 'Hallway Access'
);

/**
 * The heading and subheading an option row renders.
 *
 * Shared with `unit-option/base.tsx` rather than reimplemented, so the copy recorded on a
 * tracking event is the copy the visitor actually read.
 */
export function resolveOptionCopy({
    unitType,
    optionText,
    upgradeDescriptor,
}: ResolveOptionCopyArgs): OptionCopy {
    const access = accessLocation(unitType);
    const isDriveUp = access === 'Drive Up Access';

    const heading = (() => {
        if (upgradeDescriptor) {
            return upgradeDescriptor.name;
        }
        if (optionText?.heading) {
            return optionText.heading;
        }
        return isDriveUp ? access : `${stringifyNumber(unitType.floor)} Floor`;
    })();

    const subheading = (() => {
        if (upgradeDescriptor) {
            return upgradeDescriptor.upgradeReason ?? upgradeDescriptor.specialText ?? '';
        }
        if (optionText?.subheading) {
            return optionText.subheading;
        }
        if (isDriveUp) {
            return 'Easiest Access';
        }
        return unitType.floor === 0 ? 'Most Popular' : 'Lift Access';
    })();

    return { heading, subheading };
}
